'use client'

import React, { useRef } from 'react'

import { Box, HTMLChakraProps, chakra } from '@chakra-ui/react'
import type { SystemStyleObject } from '@chakra-ui/react'

import { CloseIcon } from '../Icon'
import {
  ColorScheme,
  disabledTabStyles,
  tabCloseStyles,
  tabListStyles,
  tabStyles,
} from './Tabs.styles'

export type { ColorScheme }

export type TabItem = {
  id: string
  label: string
  icon?: React.ReactNode
  /**
   * Blocks selection of this tab. Use while work started on another tab is
   * still running, so the operator can't navigate away from it.
   */
  disabled?: boolean
  /**
   * Render a close (X) affordance on the tab; clicking it fires onTabClose.
   * Ignored while the tab is disabled — a tab the operator can't navigate away
   * from can't be closed either.
   */
  closable?: boolean
}

type ControlledButtonProps = 'onChange' | 'role' | 'type' | 'tabIndex'

export type TabsProps = Omit<
  HTMLChakraProps<'button'>,
  ControlledButtonProps
> & {
  items: TabItem[]
  activeId: string
  onChange: (id: string) => void
  /** Called when a closable tab's close affordance is activated. */
  onTabClose?: (id: string) => void
  /** Rendered after the tabs, inside the tablist — e.g. an "add tab" button. */
  trailingAction?: React.ReactNode
  colorScheme?: ColorScheme
  tabStyleOverrides?: SystemStyleObject
  tabListStyleOverrides?: SystemStyleObject
}

/** The enabled tabs in DOM order — the ones arrow keys can move between. */
const getEnabledTabs = (tabList: HTMLElement | null): HTMLButtonElement[] =>
  Array.from(
    tabList?.querySelectorAll<HTMLButtonElement>(
      ':scope > [role="tab"]:not([disabled])'
    ) ?? []
  )

/** Where each navigation key moves focus from index `i` of `n` tabs. */
const NAVIGATION_KEYS: Record<string, (i: number, n: number) => number> = {
  ArrowRight: (i, n) => (i + 1) % n,
  ArrowLeft: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_, n) => n - 1,
}

export const Tabs = ({
  items,
  activeId,
  onChange,
  onTabClose,
  trailingAction,
  colorScheme = 'dark',
  tabStyleOverrides,
  tabListStyleOverrides,
  ...rest
}: TabsProps) => {
  const tabListRef = useRef<HTMLDivElement>(null)

  // Roving tabindex (WAI-ARIA tabs pattern): the strip is a single Tab stop on
  // the active tab, and arrow keys move between tabs. Falls back to the first
  // enabled tab when the active one is disabled or missing, so the strip is
  // always reachable.
  const isActiveFocusable = items.some(
    (item) => item.id === activeId && !item.disabled
  )
  const focusableId = isActiveFocusable
    ? activeId
    : items.find((item) => !item.disabled)?.id

  const moveFocus = (current: HTMLButtonElement, key: string) => {
    const tabs = getEnabledTabs(tabListRef.current)
    const index = tabs.indexOf(current)
    const target = NAVIGATION_KEYS[key]
    if (index < 0 || !target) return
    tabs[target(index, tabs.length)]?.focus()
  }

  // Keep focus in the strip after a keyboard close: hand it to the next tab,
  // or the previous one when closing the last, before the tab unmounts.
  const focusNeighbour = (current: HTMLButtonElement) => {
    const tabs = getEnabledTabs(tabListRef.current)
    const index = tabs.indexOf(current)
    ;(tabs[index + 1] ?? tabs[index - 1])?.focus()
  }

  return (
    <Box
      ref={tabListRef}
      role='tablist'
      {...tabListStyles(colorScheme)}
      {...tabListStyleOverrides}
    >
      {items.map((item) => {
        const isActive = item.id === activeId
        const isClosable = Boolean(
          item.closable && onTabClose && !item.disabled
        )
        return (
          <chakra.button
            aria-disabled={item.disabled}
            aria-keyshortcuts={isClosable ? 'Delete' : undefined}
            aria-selected={isActive}
            disabled={item.disabled}
            gap='4px'
            key={item.id}
            onClick={() => {
              if (!isActive && !item.disabled) onChange(item.id)
            }}
            // Arrow keys and Home/End move focus without selecting; Enter/Space
            // select the focused tab (native button). Closable tabs follow the
            // WAI-ARIA pattern: the focused tab is closed with Delete/Backspace,
            // so keyboard users never need the mouse-only close icon.
            onKeyDown={(e) => {
              if (e.key in NAVIGATION_KEYS) {
                e.preventDefault()
                moveFocus(e.currentTarget, e.key)
                return
              }
              if (isClosable && (e.key === 'Delete' || e.key === 'Backspace')) {
                e.preventDefault()
                focusNeighbour(e.currentTarget)
                onTabClose?.(item.id)
              }
            }}
            role='tab'
            tabIndex={item.id === focusableId ? 0 : -1}
            type='button'
            {...tabStyles(isActive, colorScheme)}
            {...(item.disabled ? disabledTabStyles : {})}
            {...tabStyleOverrides}
            {...rest}
          >
            {item.label}
            {item.icon}
            {isClosable ? (
              // Decorative mouse affordance only: no interactive role/tabindex,
              // so it isn't a nested control in the tab's accessibility tree.
              // Keyboard closing is handled by the tab's Delete/Backspace above.
              <chakra.span
                aria-hidden='true'
                data-testid={`tab-close-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onTabClose?.(item.id)
                }}
                // Keep the tab from taking focus when the close icon is pressed.
                onMouseDown={(e) => e.preventDefault()}
                {...tabCloseStyles(colorScheme)}
              >
                <CloseIcon colorScheme={colorScheme} size={12} />
              </chakra.span>
            ) : null}
          </chakra.button>
        )
      })}
      {trailingAction}
    </Box>
  )
}
